import React, { useState } from 'react';
import { Section } from './Section';
import { Gift, Package, Clock, Diamond, Sparkles, ChevronRight, Calendar, Flame, Check, Trophy, Filter, Download } from 'lucide-react';
interface ClaimableItem {
  id: string;
  type: 'lootbox' | 'reward' | 'booster';
  title: string;
  description: string;
  rarity: 'common' | 'rare' | 'epic' | 'legendary';
  source: string;
  expiresIn?: string;
  possibleRewards?: string[];
  amount?: number;
  amountType?: 'tokens' | 'gems' | 'xp';
  boostDuration?: string;
  boostType?: string;
}
export const Claimables: React.FC = () => {
  const [claimables, setClaimables] = useState<ClaimableItem[]>([{
    id: 'weekly-lootbox',
    type: 'lootbox',
    title: 'Weekly Premium Lootbox',
    description: 'Contains premium rewards from completing weekly rituals',
    rarity: 'epic',
    source: 'Weekly Rituals',
    possibleRewards: ['200 LuvTokens', 'XP Boost 24h', 'Premium Skin']
  }, {
    id: 'daily-chest',
    type: 'lootbox',
    title: 'Daily Reward Chest',
    description: 'Contains rewards from your daily login streak',
    rarity: 'rare',
    source: 'Daily Login',
    expiresIn: '23h 45m',
    possibleRewards: ['50 LuvTokens', '5 Gems', '1h Boost']
  }, {
    id: 'ritual-tokens',
    type: 'reward',
    title: 'Ritual Reward',
    description: 'Tokens earned from completing daily rituals',
    rarity: 'common',
    source: 'Daily Rituals',
    amount: 75,
    amountType: 'tokens'
  }, {
    id: 'premium-gems',
    type: 'reward',
    title: 'Premium Bonus',
    description: 'Gems from your premium subscription',
    rarity: 'rare',
    source: 'Premium Subscription',
    amount: 25,
    amountType: 'gems'
  }, {
    id: 'xp-booster',
    type: 'booster',
    title: 'XP Booster',
    description: 'Increases XP earned by 50%',
    rarity: 'rare',
    source: 'Weekly Challenge',
    boostDuration: '24h',
    boostType: 'XP +50%',
    expiresIn: '6d 12h'
  }]);
  const [openingLootbox, setOpeningLootbox] = useState<string | null>(null);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const handleOpenLootbox = (id: string) => {
    setOpeningLootbox(id);
    // Simulate lootbox opening
    setTimeout(() => {
      setOpeningLootbox(null);
      // Remove the lootbox from claimables
      setClaimables(prev => prev.filter(item => item.id !== id));
      // Show toast
      const reward = id === 'weekly-lootbox' ? '283 LuvTokens' : '75 LuvTokens';
      setToastMessage(`+${reward} added`);
      setShowToast(true);
      // Hide toast after 3 seconds
      setTimeout(() => {
        setShowToast(false);
      }, 3000);
    }, 1000);
  };
  const handleClaimAll = () => {
    // Simulate claiming all rewards
    const nonLootboxes = claimables.filter(item => item.type !== 'lootbox');
    const totalTokens = nonLootboxes.reduce((sum, item) => {
      if (item.amountType === 'tokens' && item.amount) {
        return sum + item.amount;
      }
      return sum;
    }, 0);
    const totalGems = nonLootboxes.reduce((sum, item) => {
      if (item.amountType === 'gems' && item.amount) {
        return sum + item.amount;
      }
      return sum;
    }, 0);
    // Remove all non-lootbox rewards
    setClaimables(prev => prev.filter(item => item.type === 'lootbox'));
    // Show toast with combined rewards
    let message = '';
    if (totalTokens > 0) message += `+${totalTokens} LuvTokens `;
    if (totalGems > 0) message += `+${totalGems} Gems `;
    if (message) {
      setToastMessage(message + 'added');
      setShowToast(true);
      // Hide toast after 3 seconds
      setTimeout(() => {
        setShowToast(false);
      }, 3000);
    }
  };
  const getRarityColor = (rarity: string) => {
    switch (rarity) {
      case 'common':
        return 'bg-gray-500 text-white';
      case 'rare':
        return 'bg-blue-500 text-white';
      case 'epic':
        return 'bg-purple-500 text-white';
      case 'legendary':
        return 'bg-amber-500 text-white';
      default:
        return 'bg-gray-500 text-white';
    }
  };
  const getRarityGlow = (rarity: string) => {
    switch (rarity) {
      case 'common':
        return 'shadow-[0_0_10px_rgba(156,163,175,0.5)]';
      case 'rare':
        return 'shadow-[0_0_10px_rgba(59,130,246,0.5)]';
      case 'epic':
        return 'shadow-[0_0_10px_rgba(139,92,246,0.5)]';
      case 'legendary':
        return 'shadow-[0_0_10px_rgba(245,158,11,0.5)]';
      default:
        return 'shadow-[0_0_10px_rgba(156,163,175,0.5)]';
    }
  };
  const getIconByType = (type: string, rarity: string) => {
    if (type === 'lootbox') {
      return rarity === 'legendary' || rarity === 'epic' ? <Gift className="text-white" data-id="element-23322" /> : <Package className="text-white" data-id="element-23323" />;
    } else if (type === 'reward') {
      return <Trophy className="text-white" data-id="element-23324" />;
    } else {
      return <Flame className="text-white" data-id="element-23325" />;
    }
  };
  const getAmountIcon = (amountType?: string) => {
    switch (amountType) {
      case 'tokens':
        return '💗';
      case 'gems':
        return '💎';
      case 'xp':
        return '✨';
      default:
        return '';
    }
  };
  const navigateToRituals = () => {
    // Navigate to rituals section
    const event = new CustomEvent('navigate', {
      detail: {
        section: 'rituals'
      }
    });
    document.dispatchEvent(event);
  };
  const nonLootboxCount = claimables.filter(item => item.type !== 'lootbox').length;
  return <div className="space-y-4 relative pb-16" data-id="element-23326">
      {claimables.length === 0 ? <div className="bg-white/80 dark:bg-gray-800/80 rounded-xl p-6 text-center shadow-sm backdrop-blur-sm border border-gray-200/50 dark:border-gray-700/50 relative overflow-hidden" data-id="element-23327">
          {/* Background pattern */}
          <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/subtle-white-feathers.png')] opacity-5 dark:opacity-10" data-id="element-23328"></div>
          <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center relative" data-id="element-23329">
            <Package className="text-gray-400 dark:text-gray-500" size={28} data-id="element-23330" />
            {/* Empty state glow effect */}
            <div className="absolute inset-0 rounded-full border border-gray-200/50 dark:border-gray-600/50 shadow-[0_0_15px_rgba(156,163,175,0.3)] animate-pulse-subtle" data-id="element-23331"></div>
          </div>
          <h3 className="text-lg font-semibold mb-2" data-id="element-23332">
            No claimables right now
          </h3>
          <p className="text-gray-600 dark:text-gray-400 mb-4" data-id="element-23333">
            Complete Rituals to unlock more rewards and lootboxes.
          </p>
          <button className="relative px-4 py-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg text-sm font-medium hover:shadow-[0_0_15px_rgba(168,85,247,0.5)] transition-all overflow-hidden group" onClick={navigateToRituals} data-id="element-23334">
            {/* Button hover effect */}
            <div className="absolute inset-0 bg-white/20 opacity-0 group-hover:opacity-100 transform translate-x-full group-hover:translate-x-0 transition-all duration-500" data-id="element-23335"></div>
            <span className="relative z-10" data-id="element-23336">Go to Rituals</span>
          </button>
        </div> : <>
          <Section title="Lootboxes" data-id="element-23337">
            <div className="grid gap-4 grid-cols-1 md:grid-cols-2" data-id="element-23338">
              {claimables.filter(item => item.type === 'lootbox').map(lootbox => <div key={lootbox.id} className="relative bg-white/80 dark:bg-gray-800/80 rounded-xl overflow-hidden shadow-sm border border-gray-200/50 dark:border-gray-700/50 backdrop-blur-sm hover:shadow-md transition-all duration-300 group" data-id="element-23339">
                    {/* Background pattern */}
                    <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/subtle-white-feathers.png')] opacity-5 dark:opacity-10" data-id="element-23340"></div>
                    {/* Top energy bar by rarity */}
                    <div className={`h-2 ${getRarityColor(lootbox.rarity)} relative overflow-hidden`} data-id="element-23341">
                      {/* Animated energy particles */}
                      <div className="absolute inset-0 flex" data-id="element-23342">
                        <div className="h-full w-[20%] bg-white/30 blur-[2px] animate-shimmer" data-id="element-23343"></div>
                      </div>
                    </div>
                    <div className="p-4 relative" data-id="element-23344">
                      <div className="flex items-start gap-4" data-id="element-23345">
                        <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${lootbox.rarity === 'legendary' ? 'bg-gradient-to-br from-amber-400 to-amber-600' : lootbox.rarity === 'epic' ? 'bg-gradient-to-br from-purple-500 to-purple-700' : lootbox.rarity === 'rare' ? 'bg-gradient-to-br from-blue-500 to-blue-700' : 'bg-gradient-to-br from-gray-500 to-gray-700'} ${getRarityGlow(lootbox.rarity)} relative overflow-hidden transform group-hover:scale-105 transition-all duration-300`} data-id="element-23346">
                          {/* Icon background glow effect */}
                          <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/diamond-upholstery.png')] opacity-20" data-id="element-23347"></div>
                          {/* Animated energy for the icon */}
                          <div className="absolute inset-0 bg-white/20 opacity-0 group-hover:opacity-100 transition-all duration-700 transform rotate-0 group-hover:rotate-180" data-id="element-23348"></div>
                          {getIconByType(lootbox.type, lootbox.rarity)}
                        </div>
                        <div className="flex-1" data-id="element-23349">
                          <div className="flex items-start justify-between" data-id="element-23350">
                            <div data-id="element-23351">
                              <h3 className="font-semibold" data-id="element-23352">{lootbox.title}</h3>
                              <p className="text-sm text-gray-600 dark:text-gray-400" data-id="element-23353">
                                {lootbox.description}
                              </p>
                            </div>
                            <span className={`text-xs px-2 py-1 rounded ${getRarityColor(lootbox.rarity)} relative overflow-hidden`} data-id="element-23354">
                              {/* Rarity badge glow effect */}
                              <div className="absolute inset-0 bg-white/20 opacity-0 group-hover:opacity-100 transition-all duration-500" data-id="element-23355"></div>
                              <span className="relative z-10" data-id="element-23356">
                                {lootbox.rarity.toUpperCase()}
                              </span>
                            </span>
                          </div>
                          {lootbox.possibleRewards && <div className="mt-3 flex flex-wrap gap-1" data-id="element-23357">
                              {lootbox.possibleRewards.map((reward, i) => <span key={i} className="text-xs bg-gray-100/80 dark:bg-gray-700/80 px-2 py-1 rounded-full backdrop-blur-sm border border-gray-200/50 dark:border-gray-600/50" data-id="element-23358">
                                  {reward}
                                </span>)}
                            </div>}
                          <div className="mt-3 flex items-center justify-between" data-id="element-23359">
                            <div className="text-xs flex items-center gap-1 text-gray-600 dark:text-gray-400" data-id="element-23360">
                              <span data-id="element-23361">From: {lootbox.source}</span>
                              {lootbox.expiresIn && <span className="flex items-center gap-1 text-amber-600 dark:text-amber-400" data-id="element-23362">
                                  <Clock size={12} data-id="element-23363" />
                                  Expires in {lootbox.expiresIn}
                                </span>}
                            </div>
                            <button className={`relative px-3 py-1.5 text-sm font-medium rounded-lg flex items-center gap-1 overflow-hidden ${openingLootbox === lootbox.id ? 'bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400' : lootbox.rarity === 'legendary' ? 'bg-gradient-to-r from-amber-500 to-amber-600 text-white' : lootbox.rarity === 'epic' ? 'bg-gradient-to-r from-purple-500 to-purple-600 text-white' : lootbox.rarity === 'rare' ? 'bg-gradient-to-r from-blue-500 to-blue-600 text-white' : 'bg-gradient-to-r from-gray-500 to-gray-600 text-white'} hover:shadow-md transition-all group-hover:shadow-[0_0_10px_rgba(168,85,247,0.3)]`} onClick={() => handleOpenLootbox(lootbox.id)} disabled={openingLootbox === lootbox.id} data-id="element-23364">
                              {/* Button hover effect */}
                              {openingLootbox !== lootbox.id && <div className="absolute inset-0 bg-white/20 opacity-0 group-hover:opacity-100 transform translate-x-full group-hover:translate-x-0 transition-all duration-500" data-id="element-23365"></div>}
                              {openingLootbox === lootbox.id ? <span className="relative z-10" data-id="element-23366">
                                  Opening...
                                </span> : <>
                                  <Sparkles size={14} className="relative z-10" data-id="element-23367" />
                                  <span className="relative z-10" data-id="element-23368">Open</span>
                                </>}
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>)}
            </div>
          </Section>
          <Section title="Rewards & Boosters" data-id="element-23369">
            <div className="grid gap-3 grid-cols-1 md:grid-cols-2" data-id="element-23370">
              {claimables.filter(item => item.type !== 'lootbox').map(reward => <div key={reward.id} className="relative bg-white/80 dark:bg-gray-800/80 rounded-lg p-4 shadow-sm border border-gray-200/50 dark:border-gray-700/50 backdrop-blur-sm hover:shadow-md transition-all duration-300 group" data-id="element-23371">
                    {/* Background pattern */}
                    <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/subtle-white-feathers.png')] opacity-5 dark:opacity-10" data-id="element-23372"></div>
                    <div className="flex items-center gap-3 relative z-10" data-id="element-23373">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${reward.type === 'booster' ? 'bg-gradient-to-br from-blue-500 to-blue-600' : reward.amountType === 'gems' ? 'bg-gradient-to-br from-cyan-500 to-cyan-600' : 'bg-gradient-to-br from-pink-500 to-pink-600'} shadow-[0_0_10px_rgba(168,85,247,0.3)] relative overflow-hidden transform group-hover:scale-105 transition-all duration-300`} data-id="element-23374">
                        {/* Icon background pattern */}
                        <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/diamond-upholstery.png')] opacity-20" data-id="element-23375"></div>
                        {/* Animated energy for the icon */}
                        <div className="absolute inset-0 bg-white/20 opacity-0 group-hover:opacity-100 transition-all duration-700 transform rotate-0 group-hover:rotate-180" data-id="element-23376"></div>
                        {reward.type === 'booster' ? <Flame className="text-white" size={18} data-id="element-23377" /> : reward.amountType === 'gems' ? <Diamond className="text-white" size={18} data-id="element-23378" /> : <Trophy className="text-white" size={18} data-id="element-23379" />}
                      </div>
                      <div className="flex-1" data-id="element-23380">
                        <div className="flex items-start justify-between" data-id="element-23381">
                          <div data-id="element-23382">
                            <h3 className="font-medium text-sm" data-id="element-23383">
                              {reward.title}
                            </h3>
                            <p className="text-xs text-gray-600 dark:text-gray-400" data-id="element-23384">
                              {reward.description}
                            </p>
                          </div>
                          {reward.type === 'reward' && reward.amount && <span className="font-semibold text-sm" data-id="element-23385">
                              {reward.amount} {getAmountIcon(reward.amountType)}
                            </span>}
                          {reward.type === 'booster' && reward.boostType && <span className="text-xs bg-blue-100/80 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 px-2 py-1 rounded backdrop-blur-sm border border-blue-200/50 dark:border-blue-800/30" data-id="element-23386">
                              {reward.boostType}
                            </span>}
                        </div>
                        <div className="mt-2 flex items-center justify-between" data-id="element-23387">
                          <div className="text-xs flex items-center gap-1 text-gray-600 dark:text-gray-400" data-id="element-23388">
                            <span data-id="element-23389">From: {reward.source}</span>
                            {reward.expiresIn && <span className="flex items-center gap-1 text-amber-600 dark:text-amber-400" data-id="element-23390">
                                <Clock size={12} data-id="element-23391" />
                                Expires in {reward.expiresIn}
                              </span>}
                            {reward.boostDuration && <span className="text-blue-600 dark:text-blue-400" data-id="element-23392">
                                Duration: {reward.boostDuration}
                              </span>}
                          </div>
                          <button className="relative text-xs px-2 py-1 bg-gradient-to-r from-green-500 to-emerald-500 text-white hover:shadow-[0_0_10px_rgba(16,185,129,0.3)] rounded-full transition-all overflow-hidden group" data-id="element-23393">
                            {/* Button hover effect */}
                            <div className="absolute inset-0 bg-white/20 opacity-0 group-hover:opacity-100 transform translate-x-full group-hover:translate-x-0 transition-all duration-500" data-id="element-23394"></div>
                            <span className="relative z-10" data-id="element-23395">Claim</span>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>)}
            </div>
          </Section>
          {/* Sticky Claim All bar */}
          {nonLootboxCount >= 2 && <div className="fixed bottom-0 inset-x-0 bg-white/90 dark:bg-gray-800/90 border-t border-gray-200/70 dark:border-gray-700/70 p-3 z-10 shadow-md backdrop-blur-sm flex justify-center" data-id="element-23396">
              <button className="relative px-6 py-2 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-lg text-sm font-medium hover:shadow-[0_0_15px_rgba(16,185,129,0.5)] transition-all flex items-center gap-2 overflow-hidden group" onClick={handleClaimAll} data-id="element-23397">
                {/* Button hover effect */}
                <div className="absolute inset-0 bg-white/20 opacity-0 group-hover:opacity-100 transform translate-x-full group-hover:translate-x-0 transition-all duration-500" data-id="element-23398"></div>
                <Check size={16} className="relative z-10" data-id="element-23399" />
                <span className="relative z-10" data-id="element-23400">Claim All Rewards</span>
              </button>
            </div>}
        </>}
      {/* Toast notification */}
      {showToast && <div className="fixed bottom-20 left-1/2 transform -translate-x-1/2 bg-gradient-to-r from-gray-900 to-gray-800 text-white px-4 py-2 rounded-lg shadow-[0_0_15px_rgba(0,0,0,0.3)] z-50 flex items-center gap-2 border border-gray-700/50 backdrop-blur-md animate-in fade-in slide-in-from-bottom-5 duration-300" data-id="element-23401">
          <Check size={16} className="text-green-500" data-id="element-23402" />
          <span data-id="element-23403">{toastMessage}</span>
        </div>}
    </div>;
};